import { ComponentLayout } from '../components/layouts/ComponentLayout'
import { Title2 } from '../components/styles/Typography'
import { theme } from '../theme/theme'
import { Grid } from '../components/Grid'
import styled from 'styled-components'
import {
  useSelectFirstName,
  useSelectLastName,
  useSelectUserBirthYear,
  useSelectUserEmail,
  useSelectUserLanguage,
  useSelectUserMarket,
} from '../state/selectors'
import { useState, useEffect } from 'react'
import { TextInput } from '../components/inputs/Text'
import { SelectInput } from '../components/inputs/Select'
import { useTranslation } from 'react-i18next'
import { dataPolicyPath } from './policy/dataPolicyPath'
import { Link } from 'react-router-dom'
import { OnClickButton } from '../components/buttons/onclick/OnClickButton'
import { useDetailUserMutation } from '../api/ester-b2b-api/private/users/userQueries'
import { isSupportedLanguage, SupportedLanguage } from '@estercare/ester-shared'
import { useUserStore } from '../state/store'
import { Alert, AlertTypes } from '../components/Alert'
import { getLanguageOptions, getDefaultLanguage } from '../utils/languageOptions'
import { env } from '../utils/environments/environmentVariables'

type AlertType = {
  message?: string
  type?: AlertTypes
}

export const Profile = () => {
  const { t, i18n } = useTranslation()
  const { setUser } = useUserStore()
  const firstName = useSelectFirstName()
  const lastName = useSelectLastName()
  const email = useSelectUserEmail()
  const birthYear = useSelectUserBirthYear()
  const userLanguage = useSelectUserLanguage()
  const userMarket = useSelectUserMarket()
  const { mutate: updateUserDetailMutation, isPending: updateUserDetailMutationisPending } = useDetailUserMutation()

  const [alertState, setAlertState] = useState<AlertType>({})
  const isProd = env.isProd('VITE_NODE_ENV')

  const [profileInfo, setProfileInfo] = useState({
    firstName: '',
    lastName: '',
    birthYear: '',
    geneticSex: '',
    email: '',
  })

  const [selectedLanguage, setSelectedLanguage] = useState(getDefaultLanguage(userLanguage, userMarket))

  const languageOptions = getLanguageOptions(t)

  useEffect(() => {
    setProfileInfo((prevState) => ({
      ...prevState,
      firstName: firstName || '',
      lastName: lastName || '',
      email: email || '',
      birthYear: birthYear?.toString() || '',
    }))
  }, [firstName, lastName, email, birthYear])

  useEffect(() => {
    if (alertState.message) {
      const timer = setTimeout(() => {
        setAlertState({
          message: undefined,
          type: undefined,
        })
      }, 5000)
      return () => clearTimeout(timer)
    }
    return () => {}
  }, [alertState.message])

  useEffect(() => {
    if (userLanguage) {
      setSelectedLanguage(userLanguage)
    }
  }, [userLanguage])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setProfileInfo((prev) => ({ ...prev, [name]: value }))
  }

  const handleSave = () => {
    const newUserData: {
      language: SupportedLanguage
      birthYear?: number
    } = {
      language: selectedLanguage,
      ...(!isProd && { birthYear: parseInt(profileInfo.birthYear, 10) }),
    }

    updateUserDetailMutation(
      {
        newUserData,
      },
      {
        onSuccess: () => {
          const currentUser = useUserStore.getState().user
          if (currentUser) {
            const updatedUser = {
              ...currentUser,
              language: selectedLanguage,
              ...(newUserData.birthYear && { birthYear: newUserData.birthYear }),
            }
            setUser(updatedUser)
            i18n.changeLanguage(selectedLanguage)
          }
          setAlertState({
            message: t('common.form.changesSaved'),
            type: 'success',
          })
        },
        onError: () => {
          setAlertState({
            message: t('common.form.saveError'),
            type: 'error',
          })
        },
      }
    )
  }

  return (
    <ComponentLayout>
      <Title2>{t('common.navigation.myAccount')}</Title2>
      <AlertWrapper>
        {alertState.message && <Alert message={alertState.message} type={alertState.type || 'success'} />}
      </AlertWrapper>
      <StyledGrid>
        <TextInput
          label={t('common.form.firstName')}
          disabled
          name="firstName"
          value={profileInfo.firstName}
          onChange={handleInputChange}
          type="text"
          placeholder={t('common.form.firstName')}
        />
        <TextInput
          label={t('common.form.lastName')}
          disabled
          name="lastName"
          value={profileInfo.lastName}
          onChange={handleInputChange}
          type="text"
          placeholder={t('common.form.lastName')}
        />
        <TextInput
          label={t('common.form.email')}
          disabled
          name="email"
          value={profileInfo.email}
          onChange={handleInputChange}
          type="email"
          placeholder={t('common.form.email')}
        />
        <TextInput
          label={t('common.form.birthYear')}
          disabled={isProd}
          name="birthYear"
          value={profileInfo.birthYear}
          onChange={handleInputChange}
          type="text"
          placeholder={t('common.form.birthYear')}
        />
        <SelectInput
          label={t('common.form.language')}
          options={languageOptions}
          value={selectedLanguage}
          onChange={(value: string) => {
            if (isSupportedLanguage(value)) {
              setSelectedLanguage(value)
            }
          }}
          placeholder={t('common.form.choose')}
        />
      </StyledGrid>
      <ButtonContainer>
        <OnClickButton
          text={t('common.form.saveChanges')}
          onClick={handleSave}
          disabled={updateUserDetailMutationisPending}
          loading={updateUserDetailMutationisPending}
        />
      </ButtonContainer>
      <PolicyLinkContainer>
        <Link to={dataPolicyPath(i18n.language)} target="_blank" rel="noopener noreferrer">
          {t('common.dataPolicy.menuLink')}
        </Link>
      </PolicyLinkContainer>
    </ComponentLayout>
  )
}

const StyledGrid = styled(Grid)`
  padding-top: ${theme.spacing.large}px;
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${theme.spacing.mediumLarge}px;
  row-gap: 20px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    grid-template-columns: 1fr;
    row-gap: 20px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${theme.spacing.medium}px;
`

const PolicyLinkContainer = styled.div`
  margin-top: ${theme.spacing.medium}px;

  a {
    color: ${theme.color.plumMid};
    text-decoration: underline;
  }
`

const AlertWrapper = styled.div`
  margin-top: ${theme.spacing.small}px;
  position: relative;
  height: 46px;
  overflow: hidden;
  margin-bottom: 0px;
`
